import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import CouponSection from '../components/Cart/CouponSection';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, cartData, removeFromCart, clearCart, getCartCount, increaseQuantity, decreaseQuantity, deliveryConfig } = useCart();

  const cartCount = getCartCount();
  const cartTotal = cartData?.totalAmt || 0;
  const totalTax = cartData?.totalTax || 0; 
  
  const isFreeDelivery = cartTotal >= (deliveryConfig?.freeDeliveryAbove || 0);
  const appliedDeliveryFee = cartData?.deliveryFee !== undefined ? cartData.deliveryFee : (isFreeDelivery ? 0 : (deliveryConfig?.deliveryFee || 0));
  
  const discountAmt = cartData?.discountAmt || 0;
  const finalTotal = cartData?.payableAmt !== undefined ? cartData.payableAmt : (cartTotal + totalTax + appliedDeliveryFee - discountAmt);

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      clearCart();
    }
  };

  const hasOOSItems = cartItems.some(item => item.stock === 0);

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert('Your cart is empty!');
      return;
    }

    if (hasOOSItems) {
      alert("Please remove out of stock items from your cart to proceed with checkout.");
      return;
    }
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <div className="py-8 max-w-6xl mx-auto px-4">
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <i className="fas fa-shopping-cart text-6xl text-gray-300 mb-5"></i>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-500 mb-6 font-medium">Looks like you haven't added any items to your cart yet.</p>
          <Link to="/quick" className="inline-block px-8 py-3 bg-[#FF7A59] text-white rounded-xl font-bold transition-colors hover:bg-orange-600 shadow-md">
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 max-w-[1200px] mx-auto px-4">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4 border-b border-gray-100 pb-4">
        <h1 className="text-2xl md:text-3xl font-black text-[#0F2F63] flex items-center gap-3">
          <i className="fas fa-shopping-cart text-[#FF7A59]"></i>
          Your Cart
          <span className="text-base text-gray-500 font-bold bg-gray-100 px-3 py-1 rounded-full">
            {cartCount} {cartCount === 1 ? 'item' : 'items'}
          </span>
        </h1>
        <button 
          onClick={handleClearCart}
          className="px-4 py-2 bg-white text-red-500 border border-red-200 rounded-lg text-sm font-bold transition-all hover:bg-red-50 hover:border-red-300 flex items-center gap-2"
        >
          <i className="fas fa-trash"></i> Clear Cart
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 lg:gap-8 items-start">
        
        {/* Cart Items List */}
        <div className="flex flex-col gap-4">
          {cartItems.map((item) => {
            const vId = String(item.cartItemId || item.outletVariantId || item.variantId || item.id || '');
            const isStandard = item.orderType?.toUpperCase() === 'STANDARD';
            const isOutOfStock = item.stock === 0;

            return (
            <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm transition-all hover:shadow-md items-center sm:items-start" key={vId}>
              
              <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden p-2 ${isOutOfStock ? 'opacity-40' : ''}`}>
                <img 
                  src={item.image || item.imageUrl || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=150&q=80'} 
                  alt="product" 
                  className="w-full h-full object-contain mix-blend-multiply" 
                />
              </div>

              <div className="flex-1 flex flex-col w-full">
                <div className={`text-base sm:text-lg font-bold text-gray-900 leading-tight mb-1 ${isOutOfStock ? 'opacity-50' : ''}`}>
                  {item.productName || item.name}
                </div>
                
                <div className={`text-xs sm:text-sm font-semibold text-gray-400 mb-2 flex items-center gap-2 ${isOutOfStock ? 'opacity-50' : ''}`}>
                  <span>{item.variantName || item.category || item.pack || '1 Pack'}</span>
                  <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                  <span className={`flex items-center gap-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider ${isStandard ? 'text-amber-500' : 'text-orange-500'}`}>
                    <i className={`fas ${isStandard ? 'fa-truck' : 'fa-bolt'}`}></i>
                    {isStandard ? 'Standard Delivery' : 'Quick Delivery'}
                  </span>
                </div>
                
                <div className="flex items-end gap-2 mb-4">
                  {/* FIXED: Removed Decimals */}
                  <span className={`text-xl font-black ${isOutOfStock ? 'text-gray-400' : 'text-[#0F2F63]'}`}>₹ {Math.round(item.sellingPrice || item.price)}</span>
                  {item.mrp > (item.sellingPrice || item.price) && (
                    <span className="text-sm font-bold text-gray-400 line-through mb-0.5">₹ {Math.round(item.mrp)}</span>
                  )}
                  {item.taxRate > 0 && !isOutOfStock && (
                    <span className="text-xs font-bold text-gray-500 mb-0.5 ml-1">+ ₹ {Math.round(item.taxRate)} Tax</span>
                  )}
                </div>
                
                <div className="flex items-center justify-between w-full mt-auto">
                  
                  {/* NEW: OOS hides +/- controls but shows Remove */}
                  {isOutOfStock ? (
                    <div className="flex items-center">
                       <span className="text-red-500 font-black text-[10px] uppercase tracking-widest border border-red-200 bg-red-50 px-2 py-1.5 rounded-lg shadow-sm">Out of Stock</span>
                    </div>
                  ) : (
                    <div className="flex items-center border-2 border-gray-200 rounded-lg overflow-hidden h-9">
                      <button 
                        onClick={() => decreaseQuantity(item)}
                        className="w-9 h-full flex items-center justify-center text-gray-600 bg-gray-50 hover:bg-gray-200 transition-colors font-bold text-lg"
                      >
                        -
                      </button>
                      <span className="w-10 text-center text-sm font-black text-gray-900">
                        {item.quantity || 1}
                      </span>
                      <button 
                        onClick={() => increaseQuantity(item)}
                        className="w-9 h-full flex items-center justify-center text-gray-600 bg-gray-50 hover:bg-gray-200 transition-colors font-bold text-lg"
                      >
                        +
                      </button>
                    </div>
                  )}

                  <button 
                    onClick={() => {
                      if (window.confirm(`Remove ${item.productName || item.name} from cart?`)) {
                        removeFromCart(item);
                      }
                    }}
                    className="text-xs font-bold text-red-500 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <i className="fas fa-trash"></i> <span className="hidden sm:inline">Remove</span>
                  </button>
                </div>

              </div>
            </div>
          )})}
        </div>

        {/* Order Summary Sidebar */}
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm sticky top-[150px]">
          
          <h3 className="text-lg font-black text-gray-900 mb-5 pb-4 border-b border-gray-100 uppercase tracking-widest">
            Order Summary
          </h3>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between text-sm font-bold text-gray-500">
              <span>Subtotal ({cartCount} items)</span>
              <span className="text-gray-900">₹ {Math.round(cartTotal)}</span>
            </div>
            
            {discountAmt > 0 && (
              <div className="flex justify-between text-sm font-bold text-emerald-500">
                <span>Coupon Discount</span>
                <span>- ₹ {Math.round(discountAmt)}</span>
              </div>
            )}

            {totalTax > 0 && (
              <div className="flex justify-between text-sm font-bold text-gray-500">
                <span>Tax</span>
                <span className="text-gray-900">₹ {Math.round(totalTax)}</span>
              </div>
            )}

            <div className="flex justify-between text-sm font-bold text-gray-500">
              <span>Delivery Fee</span>
              <span className={appliedDeliveryFee === 0 ? "text-[#2CCE75]" : "text-gray-900"}>
                {appliedDeliveryFee === 0 ? 'FREE' : `₹ ${Math.round(appliedDeliveryFee)}`}
              </span>
            </div>
          </div>
          
          {isFreeDelivery ? (
            <div className="flex items-center gap-2.5 p-3 bg-emerald-50 rounded-xl text-xs font-bold text-[#2CCE75] mb-6 border border-emerald-100">
              <i className="fas fa-check-circle text-base"></i>
              <span>Yay! Free delivery on this order!</span>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 p-3 bg-orange-50 rounded-xl text-xs font-bold text-orange-500 mb-6 border border-orange-100">
              <i className="fas fa-truck text-base"></i>
              <span>Add items worth ₹ {Math.round((deliveryConfig?.freeDeliveryAbove || 0) - cartTotal)} more for FREE delivery!</span>
            </div>
          )}

          {/* Coupon Section placed right above total amount */}
          <CouponSection />

          <div className="flex justify-between items-center py-4 border-t-2 border-gray-100 mb-6">
            <span className="text-base font-bold text-gray-500 uppercase">Total Amount</span>
            {/* FIXED: Removed Decimals */}
            <span className="text-2xl font-black text-[#0F2F63]">₹ {Math.round(finalTotal)}</span>
          </div>

          <button 
            onClick={handleCheckout}
            className={`w-full py-4 rounded-xl text-base font-black transition-all shadow-lg flex items-center justify-center gap-2 ${hasOOSItems ? 'bg-gray-300 text-gray-500 hover:bg-gray-300 cursor-not-allowed' : 'bg-[#0F2F63] hover:bg-[#1a4a8a] text-white shadow-blue-900/20'}`}
          >
            {hasOOSItems ? 'Remove OOS Items' : <><i className="fas fa-lock"></i> Proceed to Checkout</>}
          </button>
        </div>
        
      </div>
    </div>
  );
};

export default CartPage;